import { useMemo } from 'react';
import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import { resolveScopedOverride } from '@vibesites/runtime';
import {
  useGallery,
  usePropertyField,
  useSlotGroup,
  useSlotGroupOverrides,
  usePage,
  buildScopeContext,
} from '@vibesites/runtime/react';
import { RichTextContent, EditableSlotGroup } from '@vibesites/runtime/react';
import { VibesitesImage } from '@vibesites/ui-primitives';
import { pickHeroImage } from '../utils/heroImageUtils';

// The per-page `title` prop is a plain string from pages.json. Wrap it in
// <p>…</p> so it matches the DOM shape RichTextContent expects, escaping HTML
// special characters so a title containing `<` / `&` can't poison the markup.
function toRichTextDefault(text: string | undefined | null, fallback: string): string {
  if (!text) return fallback;
  const escaped = String(text)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
  return `<p>${escaped}</p>`;
}

interface PageHeroProps {
  tokenPrefix?: string;
  title?: string;
  altText?: string;
  keywords?: string[];
  pageHint?: string;
  /**
   * Skip the per-page keyword auto-pick and default to the property's featured
   * image. Set on pages hidden from the nav (reviews, privacy, sitemap, etc.),
   * where a curated per-page banner isn't worth the effort. An explicit editor
   * pick or generated image still wins.
   */
  useFeaturedImage?: boolean;
}

const SLOT_GROUP_DEF: SlotGroupDefinition = {
  label: 'Background Image',
  fields: {
    image: { label: 'Image', type: 'image', defaultField: 'property_featured_image' },
    alt: { label: 'Alt Text', type: 'text', defaultValue: 'Page banner' },
  },
  sourceBinding: {
    collection: 'photo_gallery',
    defaultIndex: 0,
    fieldMappings: { image: 'image', alt: 'alt_text' },
  },
  generator: {
    prompt:
      'Select the gallery photo that best matches this page\'s topic — e.g. attractive apartment interiors for floor-plan/apartment pages, desirable shared amenity spaces for the amenities page, gallery/lifestyle shots for the gallery page, and building or neighborhood exteriors for the neighborhood page. Avoid unappealing back-of-house shots (bathrooms/toilets, mail or package rooms, parking lots or garages, utility, storage, or mechanical areas).',
    context:
      'Full-width hero banner background at the top of an inner page; the page title is overlaid on a dark scrim, so also favor a visually strong, relatively uncluttered image.',
    sourceCollection: 'photo_gallery',
  },
};

export function PageHero({
  tokenPrefix,
  title = 'Page',
  altText = 'Page banner',
  keywords = [],
  pageHint = '',
  useFeaturedImage = false,
}: PageHeroProps) {
  const prefix = tokenPrefix || 'pagehero';
  const titlePath = `${prefix}.title`;
  const bgPath = `${prefix}.bg_image`;

  const gallery = useGallery();
  const featuredImage = usePropertyField('property_featured_image');
  const bgImage = useSlotGroup(bgPath, SLOT_GROUP_DEF);

  // `useSlotGroup` collapses two very different things into one value: an
  // explicit choice (editor pick, generator VALUE, or item swap) AND the static
  // `defaultField` fallback (`property_featured_image`). If we naively trust
  // `bgImage.image`, that single featured image wins on every page and the
  // per-page auto-pick below never runs — so all inner pages show the same
  // banner. Inspect the raw override map to tell an explicit choice apart from
  // the default, and only let an explicit choice suppress the auto-pick.
  const slotGroupOverrides = useSlotGroupOverrides();
  const page = usePage();
  const bgOverride = resolveScopedOverride(
    slotGroupOverrides,
    bgPath,
    buildScopeContext(page),
  );
  const imageExplicit = Boolean(
    bgOverride &&
      (bgOverride.sourceIndex != null || bgOverride.fieldOverrides?.image),
  );
  const altExplicit = Boolean(
    bgOverride &&
      (bgOverride.sourceIndex != null || bgOverride.fieldOverrides?.alt),
  );

  const photoIndex = useMemo(
    () => pickHeroImage(gallery, keywords, pageHint),
    [gallery, keywords, pageHint],
  );
  // `useFeaturedImage` (set on nav-hidden pages) opts out of the per-page pick.
  const autoPhoto = useFeaturedImage || photoIndex < 0 ? null : gallery[photoIndex];

  // Explicit background wins; otherwise pick a page-appropriate image per page
  // (keyword-scored, falling back to a stable per-page hash) so inner pages
  // don't all collapse to the single featured image. Nav-hidden pages skip the
  // auto-pick and just use the featured image.
  const displayImage = imageExplicit
    ? bgImage.image
    : autoPhoto?.image || featuredImage || gallery[0]?.image || null;

  const displayAlt =
    imageExplicit || altExplicit
      ? bgImage.alt || altText
      : autoPhoto?.alt_text || altText;

  return (
    <section className="relative h-[52vh] min-h-[420px] max-h-[600px] overflow-hidden">
      <EditableSlotGroup
        groupPath={bgPath}
        definition={SLOT_GROUP_DEF}
        className="absolute inset-0"
      >
        {displayImage ? (
          <VibesitesImage
            url={displayImage}
            alt={displayAlt}
            sizes="100vw"
            className="h-full w-full object-cover"
          />
        ) : (
          <div className="h-full w-full bg-[var(--vibe-color-text)]" />
        )}
        <div className="absolute inset-0 bg-black/40" />
      </EditableSlotGroup>

      {/* The site header is fixed (top:0) and overlays this banner, so pad the
          content down past the header (+ any offer banner) — mirrors the
          offset HeroSolidBG uses — keeping the title clear of the nav rather
          than tucked under it. */}
      <div
        className="relative z-10 flex h-full flex-col items-center justify-center px-4 pb-6 text-center text-white"
        style={{ paddingTop: 'calc(var(--offer-banner-height, 0px) + 5rem)' }}
      >
        <RichTextContent
          path={titlePath}
          defaultValue={toRichTextDefault(title, '<p>Page</p>')}
          as="h1"
          className="mb-3 font-heading text-4xl font-normal uppercase tracking-wider sm:text-5xl md:text-6xl"
        />
      </div>

      {/* Schema-lint anchors — hidden, never interactive */}
      <RichTextContent
        path="pagehero.title"
        defaultValue=""
        as="span"
        className="hidden"
        style={{ display: 'none' }}
      />
      <EditableSlotGroup
        groupPath="pagehero.bg_image"
        definition={SLOT_GROUP_DEF}
        className="hidden"
        style={{ display: 'none' }}
      >
        <span />
      </EditableSlotGroup>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'PageHero',
  displayName: 'Page Hero',
  tokens: {
    'pagehero.title': {
      label: 'Title',
      type: 'richtext',
      defaultValue: '<p>Page</p>',
    },
  },
  slotGroups: {
    'pagehero.bg_image': {
      label: 'Background Image',
      fields: {
        image: { label: 'Image', type: 'image', defaultField: 'property_featured_image' },
        alt: { label: 'Alt Text', type: 'text', defaultValue: 'Page banner' },
      },
      sourceBinding: {
        collection: 'photo_gallery',
        defaultIndex: 0,
        fieldMappings: { image: 'image', alt: 'alt_text' },
      },
      generator: {
        prompt:
          'Select the gallery photo that best matches this page\'s topic — e.g. attractive apartment interiors for floor-plan/apartment pages, desirable shared amenity spaces for the amenities page, gallery/lifestyle shots for the gallery page, and building or neighborhood exteriors for the neighborhood page. Avoid unappealing back-of-house shots (bathrooms/toilets, mail or package rooms, parking lots or garages, utility, storage, or mechanical areas).',
        context:
          'Full-width hero banner background at the top of an inner page; the page title is overlaid on a dark scrim, so also favor a visually strong, relatively uncluttered image.',
        sourceCollection: 'photo_gallery',
      },
    },
  },
};

(PageHero as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default PageHero;
